/**
 * RecommendationsTable - Generic table for displaying recommendations with selection
 */
import React from 'react';

export interface TableColumn<T> {
  header: string;
  key: string;
  render: (item: T, index: number) => React.ReactNode;
  width?: string;
  minWidth?: string;
}

interface RecommendationsTableProps<T> {
  items: T[];
  columns: TableColumn<T>[];
  selectedItems: Set<number>;
  onToggleItem: (index: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  itemCount: number;
  itemLabel: string; // e.g., "Keywords", "Assets"
  className?: string;
  /** Number of items before enabling scroll with sticky header (default: 15) */
  scrollThreshold?: number;
}

export function RecommendationsTable<T>({
  items,
  columns,
  selectedItems,
  onToggleItem,
  onSelectAll,
  onDeselectAll,
  itemCount,
  itemLabel,
  className = '',
  scrollThreshold = 15,
}: RecommendationsTableProps<T>) {
  // Enable scrollable container with sticky header for long lists
  const enableScroll = itemCount >= scrollThreshold;
  return (
    <div className={className}>
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-3">
        <h4 className="font-medium text-gray-900 text-sm sm:text-base">
          Recommended {itemLabel} ({itemCount})
        </h4>
        <div className="space-x-2">
          <button
            onClick={onSelectAll}
            className="text-xs sm:text-sm text-blue-600 hover:text-blue-800"
          >
            Select All
          </button>
          <button
            onClick={onDeselectAll}
            className="text-xs sm:text-sm text-blue-600 hover:text-blue-800"
          >
            Deselect All
          </button>
        </div>
      </div>

      <div className={`border rounded-md overflow-x-auto ${enableScroll ? 'max-h-96 overflow-y-auto' : ''}`}>
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className={`bg-gray-50 z-20 ${enableScroll ? 'sticky top-0' : ''}`}>
            <tr>
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Select
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  style={column.width || column.minWidth ? { width: column.width, minWidth: column.minWidth } : undefined}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-2 sm:px-4 py-2 sm:py-3">
                  <input
                    type="checkbox"
                    checked={selectedItems.has(index)}
                    onChange={() => onToggleItem(index)}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                </td>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="px-2 sm:px-4 py-2 sm:py-3"
                    style={column.minWidth ? { minWidth: column.minWidth } : undefined}
                  >
                    {column.render(item, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * RelevanceBar - Shared component for displaying relevance scores
 */
interface RelevanceBarProps {
  score: number; // 0 to 1
  className?: string;
}

export function RelevanceBar({ score, className = '' }: RelevanceBarProps) {
  return (
    <div className={`flex items-center ${className}`}>
      <div className="w-12 sm:w-24 bg-gray-200 rounded-full h-2.5 mr-2">
        <div
          className="bg-purple-600 h-2.5 rounded-full"
          style={{ width: `${(score * 100)}%` }}
        />
      </div>
      <span className="text-xs sm:text-sm font-medium">{(score * 100).toFixed(0)}%</span>
    </div>
  );
}
